/**
 * Domain vocabulary.
 *
 * These live in `@gym/core` rather than `@gym/db` because they are domain
 * concepts, not persistence details: the progression engine, the PR engine and
 * the iPhone PWA all reason about them without touching a database.
 *
 * Each is a `const` tuple so it can serve three purposes at once — a TypeScript
 * union, a runtime list for validation, and the value list for a SQL CHECK
 * constraint.
 */

/** How an exercise is performed and therefore how it is logged. */
export const MODALITIES = ['strength', 'hold', 'cardio'] as const;
export type Modality = (typeof MODALITIES)[number];

/**
 * How resistance is applied. Determines how effective load is computed and,
 * for `assisted`, which direction counts as improvement.
 */
export const LOAD_TYPES = ['external', 'bodyweight', 'bodyweight_plus', 'assisted', 'none'] as const;
export type LoadType = (typeof LOAD_TYPES)[number];

/**
 * The one authoritative check for "does this exercise use plain bodyweight
 * resistance, with no weight of its own to record?" (2026-09-16).
 *
 * Scoped deliberately to exactly `loadType === 'bodyweight'` — not
 * `bodyweight_plus` (a weighted pull-up, say, where the added weight is the
 * whole point) and not `assisted` (an assist-machine weight is still a real
 * number someone logs). Both of those keep the existing plain weight field
 * for now; this only ever narrows what already reads as "weight has no
 * meaning here," it does not touch either of them.
 *
 * Every call site that used to check a loadType/category by hand for this
 * must use this instead — see the Bodyweight Exercises feature note in
 * `LIFTX-PROJECT-CONTEXT.md` — so the definition can only ever change in one
 * place.
 */
export function isBodyweightExercise(exercise: { loadType: LoadType } | null | undefined): boolean {
  return exercise?.loadType === 'bodyweight';
}

/**
 * `warmup` never counts toward prescribed sets, volume or PRs.
 * `drop` stages attach to a primary set and never count as a prescribed set.
 */
export const SET_TYPES = ['warmup', 'normal', 'drop', 'amrap', 'failure'] as const;
export type SetType = (typeof SET_TYPES)[number];

/** Set types that count toward a prescription and are eligible for PRs. */
export const WORKING_SET_TYPES = ['normal', 'amrap', 'failure'] as const satisfies readonly SetType[];

export const SESSION_STATUSES = ['active', 'paused', 'completed', 'abandoned'] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

export const SESSION_EXERCISE_STATUSES = ['pending', 'active', 'completed', 'skipped'] as const;
export type SessionExerciseStatus = (typeof SESSION_EXERCISE_STATUSES)[number];

/** `weekly` maps days to weekdays; `rotation` cycles in order; `free` is manual only. */
export const SCHEDULE_MODES = ['weekly', 'rotation', 'free'] as const;
export type ScheduleMode = (typeof SCHEDULE_MODES)[number];

/** A block groups exercises. `superset` alternates between them (requirement G6). */
export const BLOCK_KINDS = ['single', 'superset', 'circuit'] as const;
export type BlockKind = (typeof BLOCK_KINDS)[number];

/**
 * Editor hint only. Prescriptions are always stored as per-set rows, so the
 * session engine reads one shape regardless of how they were authored.
 */
export const PRESCRIPTION_MODES = ['uniform', 'per_set'] as const;
export type PrescriptionMode = (typeof PRESCRIPTION_MODES)[number];

export const PROGRESSION_STRATEGIES = ['none', 'linear', 'double_progression'] as const;
export type ProgressionStrategy = (typeof PROGRESSION_STRATEGIES)[number];

export const PR_KINDS = [
  'rep_max',
  'est_1rm',
  'max_weight',
  'max_volume_set',
  'max_reps',
  'max_duration',
  'max_distance',
  'min_assistance',
  'best_pace',
] as const;
export type PrKind = (typeof PR_KINDS)[number];

/**
 * Record kinds where a **lower** value is the better performance.
 *
 * Everything else improves upward. Keeping the exceptions in one list means the
 * comparator can never be got wrong in one place and right in another.
 */
export const INVERTED_PR_KINDS = ['min_assistance', 'best_pace'] as const satisfies readonly PrKind[];

/** Provenance of the bodyweight recorded against a set, so stats stay honest. */
export const BODYWEIGHT_SOURCES = ['measured', 'carried_forward', 'backfilled', 'unknown'] as const;
export type BodyweightSource = (typeof BODYWEIGHT_SOURCES)[number];

export const EXERCISE_SOURCES = ['exercisedb', 'custom'] as const;
export type ExerciseSource = (typeof EXERCISE_SOURCES)[number];

export const MUSCLE_ROLES = ['primary', 'secondary'] as const;
export type MuscleRole = (typeof MUSCLE_ROLES)[number];

/** Monday = 1 … Sunday = 7, matching ISO-8601. */
export const MIN_WEEKDAY = 1;
export const MAX_WEEKDAY = 7;

/** RPE is optional, and when present is recorded on the standard 1–10 scale. */
export const MIN_RPE = 1;
export const MAX_RPE = 10;
